import { createAppointment } from "../../../../../services/api-appointment";
import { postPatients } from "../../../../../services/api-clinic";
import { TClinicPatientType, TSession } from "../../../../../utils/types";
import type { ConsultPatient } from "../hooks/useConsultSearch";

export type QuickRecordNewPatientInput = {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  sex?: string;
};

type QuickRecordAppointmentOptions = {
  startAt?: Date;
};

const formatAppointmentTime = (dateIso: string): string =>
  new Date(dateIso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const formatConsultationDate = (dateIso: string): string =>
  new Date(dateIso).toISOString().split("T")[0];

const buildPatientForConsult = (
  patient: Partial<ConsultPatient> & {
    id?: string;
    uuid?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
    sex?: string;
  },
): ConsultPatient => {
  const patientId =
    patient.patientRecordId ||
    patient.clinicOSUUID ||
    patient.uuid ||
    patient.id;
  const normalizedPatientId = patientId ? String(patientId) : undefined;

  return {
    ...patient,
    id: normalizedPatientId,
    name:
      patient.name ||
      `${patient.firstName || ""} ${patient.lastName || ""}`.trim() ||
      "Unknown Patient",
    patientRecordId:
      (patient.patientRecordId && String(patient.patientRecordId)) ||
      normalizedPatientId,
    clinicOSUUID:
      (patient.clinicOSUUID && String(patient.clinicOSUUID)) ||
      (patient.patientRecordId && String(patient.patientRecordId)) ||
      normalizedPatientId,
    hasPhotos: patient.hasPhotos ?? false,
  };
};

const createDefaultQuickRecordAppointment = async (
  session: TSession,
  patientId: string,
  options?: QuickRecordAppointmentOptions,
) => {
  const startAt = options?.startAt ?? new Date();
  const appointment = await createAppointment(session, patientId, {
    date: startAt.toISOString(),
    type: "Consultation",
    reason: "Quick Record (15 min)",
    notes: "Auto-created by Quick Record with a default 15-minute duration.",
  });

  return {
    appointment,
    appointmentTime: formatAppointmentTime(appointment.date),
    consultationDate: formatConsultationDate(appointment.date),
  };
};

export const startQuickRecordForExistingPatient = async (
  session: TSession,
  patient: ConsultPatient,
  options?: QuickRecordAppointmentOptions,
): Promise<ConsultPatient> => {
  const normalizedPatient = buildPatientForConsult(patient);
  if (!normalizedPatient.patientRecordId) {
    throw new Error("Unable to identify patient for quick recording.");
  }

  const { appointment, appointmentTime, consultationDate } =
    await createDefaultQuickRecordAppointment(
      session,
      String(normalizedPatient.patientRecordId),
      options,
    );

  return {
    ...normalizedPatient,
    appointmentId: appointment.id,
    appointmentTime,
    consultationDate,
    visitType: appointment.type,
  };
};

export const createPatientAndStartQuickRecord = async (
  session: TSession,
  input: QuickRecordNewPatientInput,
  options?: QuickRecordAppointmentOptions,
): Promise<ConsultPatient> => {
  const payload = {
    first_name: input.firstName.trim(),
    last_name: input.lastName.trim(),
    email: input.email?.trim() || undefined,
    phone: input.phone?.trim() || undefined,
    dob: input.dateOfBirth || undefined,
    sex: input.sex || undefined,
    type: TClinicPatientType.CONSULT,
  };

  const createdPatient = await postPatients(session, payload);
  const normalizedPatient = buildPatientForConsult({
    id: createdPatient.uuid,
    uuid: createdPatient.uuid,
    name: `${createdPatient.firstName || ""} ${createdPatient.lastName || ""}`.trim(),
    firstName: createdPatient.firstName,
    lastName: createdPatient.lastName,
    email: createdPatient.email,
    phone: createdPatient.phone,
    patientRecordId: createdPatient.uuid,
    clinicOSUUID: createdPatient.uuid,
    dateOfBirth: createdPatient.dateOfBirth,
    sex: createdPatient.sex,
    visitType: "Consultation",
  });

  if (!normalizedPatient.patientRecordId) {
    throw new Error("Patient was created but no patient ID was returned.");
  }

  const { appointment, appointmentTime, consultationDate } =
    await createDefaultQuickRecordAppointment(
      session,
      String(normalizedPatient.patientRecordId),
      options,
    );

  return {
    ...normalizedPatient,
    appointmentId: appointment.id,
    appointmentTime,
    consultationDate,
    visitType: appointment.type,
  };
};
